import {
    Button,
    Intent,
    SegmentedControl,
    Switch,
    Tab,
    Tabs,
    Tooltip
} from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { AgGridReact } from "ag-grid-react";
import LoadingFullScreen from "components/common/LoadingFullScreen";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import { LOADING } from "graphql/helpers/queryHelpers";
import { IN_QUEUE_PATH } from "infrastructure/RouterApp";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loading } from "types/loadingType";
import { ADMIN_TYPE } from "types/roleType";

import {
    segmentTrackCompareSelectedScenarios,
    segmentTrackExportProjectScenarios,
    segmentTrackOpenProjectPage,
    segmentTrackToggleCompareScenariosMode,
    segmentTrackToggleLatestModelsOnly
} from "./analytics";
import { ProjectCharts } from "./components/charts/ProjectCharts";
import { AdminOnlyTooltip } from "./components/common/AdminOnlyTooltip";
import { AllScenarioTooltips } from "./components/common/AllScenarioTooltips";
import { BackButton } from "./components/common/BackButton";
import { SectionHeader } from "./components/common/SectionHeader";
import { DeleteSpeculativeProjectButton } from "./components/DeleteStudyProjectButton";
import { EditableProjectTitle } from "./components/EditableProjectTitle";
import { ProjectMetricCards } from "./components/metrics/ProjectMetricCards";
import { ScenarioActionsMenu } from "./components/ScenarioActionsMenu/ScenarioActionsMenu";
import { BulkArchiveScenariosAlert } from "./components/ScenarioArchive/BulkArchiveScenariosAlert";
import { useScenarioBuilderRoutes } from "./components/ScenarioBuilder/useScenarioBuilderRoutes";
import { BulkRerunScenariosDialog } from "./components/ScenarioRerun/BulkRerunScenariosDialog";
import { SelectionBar } from "./components/SelectionBar/SelectionBar";
import { ScenariosTable } from "./components/tables/ScenariosTable";
import { useCluster, useStageConfig } from "./contexts/ClusterContext";
import { useSubscriptions } from "./contexts/InQueueSubscriptionsContext";
import {
    useProjectScenarios,
    useTrackedProject
} from "./contexts/ProjectDataContext";
import { useChromeHeightVar } from "./hooks/useChromeHeightVar";
import { useRemainingScenarioSlots } from "./hooks/useIsAtScenarioLimit";
import { useIsScenarioCreationEnabled } from "./hooks/useIsScenarioCreationEnabled";
import { useScenarioMetricsMapping } from "./hooks/useScenarioMetricsMapping";
import { useStuckToTop } from "./hooks/useStuckToTop";
import "./ProjectPage.scss";
import { Cluster, getPhaseLabel, StudyPhase } from "./types/clusterType";
import {
    InQueueSubscription,
    ProjectType,
    TrackedProject
} from "./types/projectType";
import { ScenarioMetricsMapping } from "./types/scenarioMetricsType";
import {
    ScenarioMetadata,
    ScenarioStage,
    ScenarioStatus
} from "./types/scenarioType";
import { getViewableScenarioStagesForSubscription } from "./utils/scenarios";
import {
    RERUN_ICON,
    RERUN_VERB,
    ScenarioSelectionMode
} from "./utils/scenarioSelection";
import { filterVisibleScenarios } from "./utils/scenarioVisibility";

const SCENARIOS_STICKY_CHROME_CLASS = "scenarios-sticky-chrome";

export const ProjectPage: React.FC = () => {
    const { projectId } = useParams();
    const cluster = useCluster();
    const navigate = useNavigate();
    const project = useTrackedProject({
        projectId,
        phase: cluster.studyPhase
    });

    // Future: Fetch subscription status as part of the clusters query.
    // linear.app/nira-energy/issue/NIRA-4406/reference-in-queue-clusters-in-other-schema
    const subscriptions = useSubscriptions();

    useEffect(() => {
        // Wait for project data to finish loading before deciding to redirect
        // This prevents premature redirects when URLs are pasted directly

        if (project === "loading") {
            return;
        }

        if (project === undefined) {
            navigate(`/${IN_QUEUE_PATH}`);
        }
    }, [project, navigate]);

    const scenarios = useProjectScenarios({
        projectId,
        phase: cluster.studyPhase
    });
    const stageConfig = useStageConfig();

    if (
        !project ||
        project === LOADING ||
        !scenarios ||
        scenarios === LOADING ||
        !cluster ||
        !stageConfig ||
        subscriptions === LOADING ||
        subscriptions === undefined
    ) {
        return <LoadingFullScreen />;
    }
    const subscription = subscriptions.find(
        (s) => s.subscriptionId === project.subscriptionId
    );
    if (!subscription) {
        console.warn(
            `Subscription ${project.subscriptionId} not found for project ${project.projectId}`
        );
    }
    return (
        <LoadedProjectPage
            cluster={cluster}
            project={project}
            scenarios={scenarios}
            subscription={subscription}
        />
    );
};

const LoadedProjectPage: React.FC<{
    cluster: Cluster;
    project: TrackedProject;
    scenarios: ScenarioMetadata[];
    subscription: InQueueSubscription | undefined;
}> = ({ cluster, project, scenarios, subscription }) => {
    const [showArchived, setShowArchived] = useState(false);
    const navigate = useNavigate();
    const [selectionMode, setSelectionMode] = useState<
        ScenarioSelectionMode | undefined
    >();
    const [selectedScenarios, setSelectedScenarios] = useState<
        ReadonlyArray<ScenarioMetadata>
    >([]);
    const [isBulkArchiveAlertOpen, setIsBulkArchiveAlertOpen] = useState(false);
    const [isRerunDialogOpen, setIsRerunDialogOpen] = useState(false);
    const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);
    const { role } = useUserDataContext();
    useEffect(() => {
        segmentTrackOpenProjectPage({ projectId: project.projectId, cluster });
    }, []);

    const availableScenarioStages = useMemo(
        () => getViewableScenarioStagesForSubscription(cluster, subscription),
        [cluster, subscription]
    );

    // If any decision point scenarios exist, default to show those
    const [scenarioStage, setScenarioStage] = useState<ScenarioStage>(
        availableScenarioStages.has("DECISION_POINT")
            ? "DECISION_POINT"
            : "PREVIEW"
    );

    const scenarioCreationEnabled = useIsScenarioCreationEnabled({
        stage: scenarioStage
    });
    const scenarioBuilderRoutes = useScenarioBuilderRoutes();

    const numArchivedScenarios = scenarios.filter(
        (s) => s.stage === scenarioStage && s.archived
    ).length;

    const filteredScenarios = filterVisibleScenarios(
        scenarios,
        scenarioStage,
        showArchived
    );

    const gridRef = useRef<AgGridReact<ScenarioMetadata>>(null);
    const tableWrapperRef = useRef<HTMLDivElement | null>(null);

    const allCompletedScenarios = scenarios.filter(
        (s) => s.status === ScenarioStatus.RunSucceeded
    );
    const scenarioMetrics = useScenarioMetricsMapping(
        project,
        allCompletedScenarios
    );

    const onClickExport = () => {
        gridRef.current?.api.exportDataAsCsv({
            exportedRows: "all",
            fileName: `Nira Export - Project ${project.projectId} scenarios`,
            // Honor each column's `useValueFormatterForExport`. Cost columns
            // set it `false` so they export raw numbers (Excel reads them as
            // numbers, not "$109K"/"250M" strings); every other column keeps
            // its display formatting (Status, Rank, Sensitivities, etc.).
            processCellCallback: (params) =>
                params.column.getColDef().useValueFormatterForExport === false
                    ? params.value
                    : params.formatValue(params.value)
        });

        segmentTrackExportProjectScenarios({
            projectId: project.projectId,
            numScenarios: filteredScenarios.length
        });
    };

    const readyToCompare =
        selectionMode === ScenarioSelectionMode.Compare &&
        selectedScenarios.length === 2;
    const compareSelectedScenarios = () => {
        const [first, second] = selectedScenarios;
        segmentTrackCompareSelectedScenarios({
            projectId: project.projectId,
            firstScenarioId: first.scenarioId,
            secondScenarioId: second.scenarioId
        });
        navigate(
            `compare-scenarios?firstScenarioId=${first.scenarioId}&secondScenarioId=${second.scenarioId}`
        );
    };
    const scrollGridIntoView = () => {
        requestAnimationFrame(() => {
            const table = tableWrapperRef.current;
            if (!table) return;

            // Anything above the chrome's bottom edge is covered by it.
            const chromeBottom =
                document
                    .querySelector(`.${SCENARIOS_STICKY_CHROME_CLASS}`)
                    ?.getBoundingClientRect().bottom ?? 0;
            const { height } = table.getBoundingClientRect();
            const fillsTheScreen = height >= window.innerHeight - chromeBottom;

            table.scrollIntoView({
                behavior: "smooth",
                block: fillsTheScreen ? "start" : "nearest"
            });
        });
    };

    const changeSelectionMode = (next: ScenarioSelectionMode | undefined) => {
        const wasComparing = selectionMode === ScenarioSelectionMode.Compare;
        const isComparing = next === ScenarioSelectionMode.Compare;
        if (wasComparing !== isComparing) {
            segmentTrackToggleCompareScenariosMode({
                projectId: project.projectId,
                enabled: isComparing
            });
        }
        setSelectionMode(next);
        setIsActionsMenuOpen(false);
        if (next !== undefined) {
            scrollGridIntoView();
        }
    };

    const remainingScenarioSlots = useRemainingScenarioSlots();
    const rerunExceedsScenarioLimit =
        remainingScenarioSlots !== undefined &&
        selectedScenarios.length > remainingScenarioSlots;

    const onCancelSelectionMode = () => changeSelectionMode(undefined);

    const headerActions = (
        <div className="scenarios-section-header">
            {numArchivedScenarios > 0 && (
                <Switch
                    checked={showArchived}
                    label={`Show archived (${numArchivedScenarios})`}
                    onChange={() => setShowArchived(!showArchived)}
                    alignIndicator={"right"}
                    innerLabelChecked={"on"}
                    innerLabel={"off"}
                    className="show-archived-checkbox"
                />
            )}
            <Button
                text="Export"
                icon={IconNames.EXPORT}
                variant="minimal"
                onClick={onClickExport}
            />
            <ScenarioActionsMenu
                scenarioStage={scenarioStage}
                selectionMode={selectionMode}
                isOpen={isActionsMenuOpen}
                setIsOpen={setIsActionsMenuOpen}
                onSelectMode={changeSelectionMode}
            />
            <AllScenarioTooltips stage={scenarioStage}>
                <Button
                    icon={IconNames.ADD}
                    text="Create scenario"
                    intent={Intent.PRIMARY}
                    onClick={() =>
                        navigate(
                            scenarioBuilderRoutes.createHref(scenarioStage)
                        )
                    }
                    disabled={!scenarioCreationEnabled}
                />
            </AllScenarioTooltips>
        </div>
    );

    return (
        <>
            <BackButton
                text="Back to portfolio"
                to={`/${IN_QUEUE_PATH}/${cluster.slug}`}
            />
            <div>
                <SectionHeader
                    level="h1"
                    title={<EditableProjectTitle project={project} />}
                    actions={
                        project.type === ProjectType.SPECULATIVE_PROJECT ? (
                            <DeleteSpeculativeProjectButton
                                project={project}
                                scenarios={scenarios}
                            />
                        ) : undefined
                    }
                />
                <Tabs
                    selectedTabId={scenarioStage}
                    onChange={(newTabId) =>
                        setScenarioStage(newTabId as ScenarioStage)
                    }
                    size="large"
                    fill
                >
                    {cluster.studyPhase === StudyPhase.PRESCREEN && (
                        // we have no concept of Preview vs DP for pre-screen
                        <Tab
                            id="PREVIEW"
                            title={getPhaseLabel(cluster.studyPhase)}
                        />
                    )}
                    {cluster.studyPhase !== StudyPhase.PRESCREEN && (
                        <Tab
                            id="PREVIEW"
                            title={`${getPhaseLabel(
                                cluster.studyPhase
                            )} Preview`}
                            disabled={!availableScenarioStages.has("PREVIEW")}
                        />
                    )}
                    {cluster.studyPhase !== StudyPhase.PRESCREEN && (
                        <Tab
                            id="DECISION_POINT"
                            title={`${getPhaseLabel(
                                cluster.studyPhase
                            )} Decision Point`}
                            disabled={
                                !availableScenarioStages.has("DECISION_POINT")
                            }
                        />
                    )}
                </Tabs>
            </div>
            <div className="project-page-body">
                <ProjectPageBody
                    project={project}
                    scenarios={scenarios}
                    scenarioStage={scenarioStage}
                    showArchived={showArchived}
                    headerActions={headerActions}
                    gridRef={gridRef}
                    tableWrapperRef={tableWrapperRef}
                    scenarioMetrics={scenarioMetrics}
                    selectionMode={selectionMode}
                    onSelectedScenariosChange={setSelectedScenarios}
                />
                {selectionMode === ScenarioSelectionMode.Compare && (
                    <SelectionBar
                        itemName="scenario"
                        selectedCount={selectedScenarios.length}
                        emptyLabel="Select two scenarios to compare"
                        onClear={() => gridRef.current?.api?.deselectAll()}
                        onCancel={onCancelSelectionMode}
                    >
                        <Button
                            icon={IconNames.COMPARISON}
                            text="Compare selected scenarios"
                            intent={readyToCompare ? Intent.SUCCESS : undefined}
                            disabled={!readyToCompare}
                            onClick={compareSelectedScenarios}
                        />
                    </SelectionBar>
                )}
                {selectionMode === ScenarioSelectionMode.Rerun && (
                    <SelectionBar
                        itemName="scenario"
                        selectedCount={selectedScenarios.length}
                        emptyLabel="Select the scenarios to duplicate and re-run"
                        onClear={() => gridRef.current?.api?.deselectAll()}
                        onCancel={onCancelSelectionMode}
                    >
                        <AllScenarioTooltips stage={scenarioStage}>
                            <Tooltip
                                content={`Only ${remainingScenarioSlots} more ${
                                    remainingScenarioSlots === 1
                                        ? "scenario"
                                        : "scenarios"
                                } can be created on this subscription. Deselect ${
                                    selectedScenarios.length -
                                    (remainingScenarioSlots ?? 0)
                                } to continue.`}
                                disabled={!rerunExceedsScenarioLimit}
                                position="top"
                            >
                                <Button
                                    icon={RERUN_ICON}
                                    text={`${RERUN_VERB}${
                                        selectedScenarios.length > 0
                                            ? ` (${selectedScenarios.length})`
                                            : ""
                                    }`}
                                    intent={Intent.PRIMARY}
                                    disabled={
                                        selectedScenarios.length === 0 ||
                                        !scenarioCreationEnabled ||
                                        rerunExceedsScenarioLimit
                                    }
                                    onClick={() => setIsRerunDialogOpen(true)}
                                />
                            </Tooltip>
                        </AllScenarioTooltips>
                    </SelectionBar>
                )}
                {selectionMode === ScenarioSelectionMode.Archive && (
                    <SelectionBar
                        itemName="scenario"
                        selectedCount={selectedScenarios.length}
                        emptyLabel="Select scenarios to archive"
                        onClear={() => gridRef.current?.api?.deselectAll()}
                        onCancel={onCancelSelectionMode}
                    >
                        <AdminOnlyTooltip>
                            <Button
                                icon={IconNames.ARCHIVE}
                                text={`Archive${
                                    selectedScenarios.length > 0
                                        ? ` (${selectedScenarios.length})`
                                        : ""
                                }`}
                                intent={Intent.WARNING}
                                disabled={
                                    selectedScenarios.length === 0 ||
                                    role !== ADMIN_TYPE
                                }
                                onClick={() => setIsBulkArchiveAlertOpen(true)}
                            />
                        </AdminOnlyTooltip>
                    </SelectionBar>
                )}
                <BulkArchiveScenariosAlert
                    isOpen={isBulkArchiveAlertOpen}
                    setIsOpen={setIsBulkArchiveAlertOpen}
                    scenarios={selectedScenarios}
                    projectId={project.projectId}
                    onArchived={() => setSelectionMode(undefined)}
                />
                <BulkRerunScenariosDialog
                    isOpen={isRerunDialogOpen}
                    setIsOpen={setIsRerunDialogOpen}
                    scenarios={selectedScenarios}
                    project={project}
                    onRerun={() => setSelectionMode(undefined)}
                />
            </div>
        </>
    );
};

const projectScenarioMetrics = (
    metrics: Loading<ScenarioMetricsMapping>,
    scenarioIds: ReadonlySet<string>
): Loading<ScenarioMetricsMapping> => {
    if (metrics === "loading") return "loading";
    const out: ScenarioMetricsMapping = {};
    scenarioIds.forEach((id) => {
        if (metrics[id] !== undefined) {
            out[id] = metrics[id];
        }
    });
    return out;
};

const ProjectPageBody: React.FC<{
    project: TrackedProject;
    scenarios: ScenarioMetadata[];
    scenarioStage: ScenarioStage;
    showArchived: boolean;
    headerActions: JSX.Element;
    gridRef: React.RefObject<AgGridReact<ScenarioMetadata>>;
    tableWrapperRef: React.MutableRefObject<HTMLDivElement | null>;
    scenarioMetrics: Loading<ScenarioMetricsMapping>;
    selectionMode?: ScenarioSelectionMode;
    onSelectedScenariosChange?: (
        selected: ReadonlyArray<ScenarioMetadata>
    ) => void;
}> = ({
    project,
    scenarios,
    scenarioStage,
    showArchived,
    headerActions,
    gridRef,
    tableWrapperRef,
    scenarioMetrics,
    selectionMode,
    onSelectedScenariosChange
}) => {
    const [onlyLatestModel, setOnlyLatestModel] = useState(false);

    const hostRef = useRef<HTMLDivElement | null>(null);
    const chromeRef = useRef<HTMLDivElement | null>(null);
    useChromeHeightVar(hostRef, chromeRef, true);
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const chromeStuck = useStuckToTop(sentinelRef, true);

    const visibleScenarios = filterVisibleScenarios(
        scenarios,
        scenarioStage,
        showArchived
    );
    // Only offer the toggle when it would actually change what's shown.
    const hasStaleScenarios = visibleScenarios.some((s) => s.isStale);
    const filteredScenarios = onlyLatestModel
        ? visibleScenarios.filter((s) => !s.isStale)
        : visibleScenarios;

    const completedScenarios = filteredScenarios.filter(
        (s) => s.status === ScenarioStatus.RunSucceeded
    );
    const completedScenarioIds = new Set(
        completedScenarios.map((s) => s.scenarioId)
    );
    const filteredMetrics = projectScenarioMetrics(
        scenarioMetrics,
        completedScenarioIds
    );

    return (
        <div ref={hostRef}>
            <div ref={sentinelRef} className="scenarios-sticky-sentinel" />
            <div
                ref={chromeRef}
                className={
                    chromeStuck
                        ? `${SCENARIOS_STICKY_CHROME_CLASS} is-stuck`
                        : SCENARIOS_STICKY_CHROME_CLASS
                }
            >
                <SectionHeader
                    level="h2"
                    title={
                        <div className="scenarios-title-row">
                            <span className="scenarios-title">Scenarios</span>
                            {hasStaleScenarios && (
                                <SegmentedControl
                                    small
                                    value={onlyLatestModel ? "LATEST" : "ALL"}
                                    onValueChange={(v) => {
                                        const enabled = v === "LATEST";
                                        segmentTrackToggleLatestModelsOnly({
                                            projectId: project.projectId,
                                            enabled
                                        });
                                        setOnlyLatestModel(enabled);
                                    }}
                                    options={[
                                        {
                                            label: "Latest models",
                                            value: "LATEST"
                                        },
                                        {
                                            label: "All scenarios",
                                            value: "ALL"
                                        }
                                    ]}
                                />
                            )}
                        </div>
                    }
                    actions={headerActions}
                />
            </div>
            <ProjectMetricCards scenarioMetrics={filteredMetrics} />
            <ProjectCharts
                scenarios={completedScenarios}
                allScenarios={filteredScenarios}
                project={project}
                scenarioMetrics={scenarioMetrics}
            />
            <ScenariosTable
                project={project}
                scenarios={filteredScenarios}
                scenarioMetrics={scenarioMetrics}
                gridRef={gridRef}
                wrapperRef={tableWrapperRef}
                scenarioStage={scenarioStage}
                selectionMode={selectionMode}
                onSelectedScenariosChange={onSelectedScenariosChange}
            />
        </div>
    );
};
